import { cashReportSchema } from './cash-sessions';
import { z } from 'zod';
import { intish, isoDateTime } from './common';
import { wireTerminalSchema, normalizeTerminal, type Terminal } from './terminals';

/**
 * Day-session endpoints (pos-api/app/Http/Controllers/Api/POS/DaySessionController.php).
 * All use the `{ success, message?, data }` envelope; the index adds flattened
 * pagination keys at the top level. There are NO cash-float fields — start
 * takes only terminal/branch/notes.
 */

export const wireDaySessionSchema = z.looseObject({
  cash_state: z.enum(['trading', 'counting', 'review', 'closed']).nullish(),
  currency: z.string().nullish(),
  id: z.number(),
  terminal_id: z.number(),
  branch_id: z.number().nullable(),
  user_id: z.number().nullish(),
  started_at: isoDateTime,
  ended_at: isoDateTime.nullable(),
  status: z.enum(['open', 'closed']),
  notes: z.string().nullish(),
  /** loaded on start/current/end responses (never `user` — cross-DB relation). */
  terminal: wireTerminalSchema.nullish(),
});
export type WireDaySession = z.infer<typeof wireDaySessionSchema>;

/** start 201 / current 200 / end 200 envelope; current returns data:null when no open session. */
export const wireSessionEnvelopeSchema = z.looseObject({
  success: z.boolean(),
  message: z.string().optional(),
  data: wireDaySessionSchema.nullable(),
});

/** GET /day-sessions/{id}/summary — plain JSON numbers, NOT decimal strings (DaySessionController.php:235-285). */
export const wireDaySessionSummarySchema = z.looseObject({
  cash_reconciliation: cashReportSchema.optional(),
  date: z.string(),
  day_session_id: z.number(),
  terminal_id: z.number(),
  terminal_name: z.string().nullable(),
  branch_id: z.number().nullable(),
  branch_name: z.string().nullable(),
  started_at: isoDateTime,
  ended_at: isoDateTime.nullable(),
  duration: z.string(),
  gross_sales: z.number(),
  total_delivery_charges: z.number(),
  total_discount: z.number(),
  net_sales: z.number(),
  total_orders: z.number(),
  cancelled_orders: z.number(),
  refund_amount: z.number(),
  items_sold: z.number(),
  average_order_value: z.number(),
  payment_methods: z.looseObject({
    cash: z.number(),
    card: z.number(),
    mobile: z.number(),
    other: z.number(),
  }),
  /** keys are dine_in/pickup/delivery even though the order enum value is `dinein`. */
  order_count_by_type: z.looseObject({
    dine_in: z.number(),
    pickup: z.number(),
    delivery: z.number(),
  }),
  platform_breakdown: z.record(
    z.string(),
    z.looseObject({ orders: z.number(), sales: z.number() }),
  ),
  product_sales: z.array(
    z.looseObject({
      product_id: z.number(),
      product_name: z.string(),
      sku: z.string().nullable(),
      category: z.string().nullable(),
      quantity_sold: z.number(),
      unit_price: z.number(),
      average_price: z.number(),
      total_revenue: z.number(),
    }),
  ),
});
export type DaySessionSummary = z.infer<typeof wireDaySessionSummarySchema>;

export const wireSessionSummaryEnvelopeSchema = z.looseObject({
  success: z.boolean(),
  data: wireDaySessionSummarySchema,
});

/** GET /day-sessions index rows — flattened, with LEFT-JOIN aggregates (DaySessionController.php:352-369). */
export const wireSessionListRowSchema = z.looseObject({
  id: z.number(),
  terminal_id: z.number(),
  terminal_name: z.string().nullish(),
  branch_id: z.number().nullable(),
  user_id: z.number().nullish(),
  started_at: isoDateTime,
  ended_at: isoDateTime.nullable(),
  status: z.enum(['open', 'closed']),
  notes: z.string().nullish(),
  total_sales: z.number(),
  total_orders: z.number(),
});
export type WireSessionListRow = z.infer<typeof wireSessionListRowSchema>;

export const wireSessionsIndexSchema = z.looseObject({
  success: z.boolean(),
  data: z.array(wireSessionListRowSchema),
  total: z.number(),
  per_page: intish,
  current_page: z.number(),
  last_page: z.number(),
});

// ---------------------------------------------------------------------------
// Normalized domain types
// ---------------------------------------------------------------------------

export interface DaySession {
  cash_state?: 'trading' | 'counting' | 'review' | 'closed' | null;
  currency?: string | null;
  id: number;
  terminal_id: number;
  branch_id: number | null;
  user_id: number | null;
  started_at: string;
  ended_at: string | null;
  status: 'open' | 'closed';
  notes: string | null;
  terminal: Terminal | null;
}

export interface DaySessionListRow {
  id: number;
  terminal_id: number;
  terminal_name: string | null;
  branch_id: number | null;
  user_id: number | null;
  started_at: string;
  ended_at: string | null;
  status: 'open' | 'closed';
  notes: string | null;
  total_sales: number;
  total_orders: number;
}

export interface DaySessionList {
  sessions: DaySessionListRow[];
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

export function normalizeDaySession(wire: WireDaySession): DaySession {
  return {
    cash_state: wire.cash_state,
    currency: wire.currency,
    id: wire.id,
    terminal_id: wire.terminal_id,
    branch_id: wire.branch_id,
    user_id: wire.user_id ?? null,
    started_at: wire.started_at,
    ended_at: wire.ended_at,
    status: wire.status,
    notes: wire.notes ?? null,
    terminal: wire.terminal ? normalizeTerminal(wire.terminal) : null,
  };
}

export function normalizeSessionListRow(wire: WireSessionListRow): DaySessionListRow {
  return {
    id: wire.id,
    terminal_id: wire.terminal_id,
    terminal_name: wire.terminal_name ?? null,
    branch_id: wire.branch_id,
    user_id: wire.user_id ?? null,
    started_at: wire.started_at,
    ended_at: wire.ended_at,
    status: wire.status,
    notes: wire.notes ?? null,
    total_sales: wire.total_sales,
    total_orders: wire.total_orders,
  };
}
